import React, { useState, useEffect, useCallback, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip
} from "@material-ui/core";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  PictureAsPdf as PdfIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import FiscalOrderModal from "../../components/FiscalOrderModal";
import FiscalSettingsModal from "../../components/FiscalSettingsModal";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    margin: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  statsCard: {
    minHeight: 100,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  statsValue: {
    fontSize: "1.8rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  statsLabel: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  searchField: {
    marginRight: theme.spacing(2),
    width: 300,
  },
  filterSelect: {
    minWidth: 150,
    marginRight: theme.spacing(2),
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
}));

const statusColors = {
  pendente: "default",
  emitido: "primary",
  cancelado: "secondary",
  erro: "secondary",
};

const statusLabels = {
  pendente: "Pendente",
  emitido: "Emitido",
  cancelado: "Cancelado",
  erro: "Erro",
};

const Fiscal = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pendentes: 0,
    emitidos: 0,
    valorTotal: 0,
  });

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fiscal/orders", {
        params: { searchParam, pageNumber, status: statusFilter },
      });
      setOrders(data.orders);
      setTotalCount(data.count);
      setHasMore(data.hasMore);

      // Calcular stats simples
      setStats({
        total: data.count,
        pendentes: data.orders.filter((o) => o.status === "pendente").length,
        emitidos: data.orders.filter((o) => o.status === "emitido").length,
        valorTotal: data.orders.reduce(
          (sum, o) => sum + Number(o.serviceValue || 0),
          0
        ),
      });
    } catch (err) {
      toast.error("Erro ao carregar pedidos fiscais.");
    }
    setLoading(false);
  }, [searchParam, pageNumber, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleOpenOrderModal = (order = null) => {
    setSelectedOrder(order);
    setOrderModalOpen(true);
  };

  const handleCloseOrderModal = () => {
    setSelectedOrder(null);
    setOrderModalOpen(false);
    fetchOrders();
  };

  const handleDeleteOrder = async () => {
    if (!deletingOrder) return;
    try {
      await api.delete(`/fiscal/orders/${deletingOrder.id}`);
      toast.success("Pedido excluído com sucesso!");
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao excluir pedido.");
    }
    setDeletingOrder(null);
    setConfirmModalOpen(false);
  };

  const handleEmitirNfse = async (orderId) => {
    try {
      await api.post(`/fiscal/orders/${orderId}/emitir`);
      toast.success("NFS-e emitida com sucesso!");
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao emitir NFS-e.");
    }
  };

  const handleDownloadPdf = async (orderId) => {
    try {
      const response = await api.get(`/fiscal/orders/${orderId}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `nfse_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error("Erro ao baixar PDF da NFS-e.");
    }
  };

  const handleCancelarNfse = async (orderId) => {
    if (!window.confirm("Tem certeza que deseja cancelar esta NFS-e?")) return;
    try {
      await api.post(`/fiscal/orders/${orderId}/cancelar`);
      toast.success("NFS-e cancelada com sucesso!");
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao cancelar NFS-e.");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("pt-BR");
  };

  return (
    <Paper className={classes.mainPaper} variant="outlined">
      {/* Header */}
      <div className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: "bold" }}>
          Módulo Fiscal - NFS-e
        </Typography>
        <div className={classes.actionButtons}>
          <Button
            variant="outlined"
            color="default"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsModalOpen(true)}
          >
            Configurações
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenOrderModal()}
          >
            Novo Pedido
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent className={classes.statsCard}>
              <Typography className={classes.statsValue}>{stats.total}</Typography>
              <Typography className={classes.statsLabel}>Total de Pedidos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent className={classes.statsCard}>
              <Typography className={classes.statsValue}>{stats.pendentes}</Typography>
              <Typography className={classes.statsLabel}>Pendentes</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent className={classes.statsCard}>
              <Typography className={classes.statsValue}>{stats.emitidos}</Typography>
              <Typography className={classes.statsLabel}>Emitidos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent className={classes.statsCard}>
              <Typography className={classes.statsValue}>
                {formatCurrency(stats.valorTotal)}
              </Typography>
              <Typography className={classes.statsLabel}>Valor Total</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <div style={{ display: "flex", marginBottom: 16, alignItems: "center" }}>
        <TextField
          className={classes.searchField}
          placeholder="Buscar por número, cliente, CPF/CNPJ..."
          variant="outlined"
          size="small"
          value={searchParam}
          onChange={(e) => setSearchParam(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl variant="outlined" size="small" className={classes.filterSelect}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="pendente">Pendente</MenuItem>
            <MenuItem value="emitido">Emitido</MenuItem>
            <MenuItem value="cancelado">Cancelado</MenuItem>
            <MenuItem value="erro">Erro</MenuItem>
          </Select>
        </FormControl>
        <IconButton onClick={fetchOrders}>
          <RefreshIcon />
        </IconButton>
      </div>

      {/* Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nº Pedido</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>CPF/CNPJ</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Grupo/Cota</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>NFS-e</TableCell>
              <TableCell>Data</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>{order.orderNumber}</TableCell>
                <TableCell>{order.customerName}</TableCell>
                <TableCell>{order.customerDocument}</TableCell>
                <TableCell>{formatCurrency(order.serviceValue)}</TableCell>
                <TableCell>
                  {order.consortiumGroup && order.consortiumQuota
                    ? `${order.consortiumGroup}/${order.consortiumQuota}`
                    : "-"}
                </TableCell>
                <TableCell>
                  <Chip
                    label={statusLabels[order.status] || order.status}
                    color={statusColors[order.status] || "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>{order.nfseNumber || "-"}</TableCell>
                <TableCell>{formatDate(order.createdAt)}</TableCell>
                <TableCell align="center">
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    {order.status === "pendente" && (
                      <>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenOrderModal(order)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Emitir NFS-e">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEmitirNfse(order.id)}
                          >
                            <ReceiptIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeletingOrder(order);
                              setConfirmModalOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" color="secondary" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {order.status === "emitido" && (
                      <>
                        <Tooltip title="Baixar PDF">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDownloadPdf(order.id)}
                          >
                            <PdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancelar NFS-e">
                          <IconButton
                            size="small"
                            onClick={() => handleCancelarNfse(order.id)}
                          >
                            <CancelIcon fontSize="small" color="secondary" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {order.status === "erro" && (
                      <>
                        <Tooltip title="Editar e Reemitir">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenOrderModal(order)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Tentar Emitir Novamente">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEmitirNfse(order.id)}
                          >
                            <ReceiptIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Nenhum pedido fiscal encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modals */}
      <FiscalOrderModal
        open={orderModalOpen}
        onClose={handleCloseOrderModal}
        order={selectedOrder}
      />
      <FiscalSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
      />
      <ConfirmationModal
        title="Excluir Pedido"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDeleteOrder}
      >
        Tem certeza que deseja excluir o pedido {deletingOrder?.orderNumber}?
      </ConfirmationModal>
    </Paper>
  );
};

export default Fiscal;
